import { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Grid,
  CircularProgress,
  Autocomplete,
  createFilterOptions,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle, Cancel } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { authApi, getErrorMessage, serviceTypesApi, PublicServiceType } from '../services/api';
import { COLORS, RESERVED_SLUGS } from '@elevair/shared';

// Type for autocomplete options (includes custom "add new" option)
interface ServiceTypeOption extends PublicServiceType {
  inputValue?: string;
  isCustom?: boolean;
}

const filter = createFilterOptions<ServiceTypeOption>({
  matchFrom: 'start',
  stringify: (option) => option.name,
});

// Stricter email validation regex that catches common invalid formats
// Requires: local part, @, domain, dot, TLD (2+ chars)
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const registerSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z
    .string()
    .min(1, 'Email is required')
    .refine(
      (email) => !email.includes(' '),
      { message: 'Please enter a valid email address' }
    )
    .refine(
      (email) => emailRegex.test(email.trim()),
      { message: 'Please enter a valid email address' }
    ),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  organizationName: z.string().min(1, 'Business name is required'),
  organizationSlug: z
    .string()
    .min(3, 'URL must be at least 3 characters')
    .max(50, 'URL must be at most 50 characters')
    .regex(
      /^[a-z0-9-]+$/,
      'URL can only contain lowercase letters, numbers, and hyphens'
    ),
  serviceTypeId: z.string().min(1, 'Service type is required'),
});

type RegisterFormData = z.infer<typeof registerSchema>;

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register: registerUser } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const [serviceTypes, setServiceTypes] = useState<PublicServiceType[]>([]);
  const [serviceTypesLoading, setServiceTypesLoading] = useState(true);
  const [selectedServiceType, setSelectedServiceType] = useState<ServiceTypeOption | null>(null);
  const [serviceTypeInput, setServiceTypeInput] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const organizationName = watch('organizationName');
  const organizationSlug = watch('organizationSlug');

  // Fetch service types on mount
  useEffect(() => {
    const fetchServiceTypes = async () => {
      try {
        const response = await serviceTypesApi.list();
        // Sort alphabetically by name
        const sorted = [...response.serviceTypes].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setServiceTypes(sorted);
      } catch (err) {
        console.error('Failed to fetch service types:', err);
      } finally {
        setServiceTypesLoading(false);
      }
    };
    fetchServiceTypes();
  }, []);

  // Auto-generate slug from business name in real-time
  useEffect(() => {
    if (organizationName && !slugManuallyEdited) {
      const slug = organizationName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setValue('organizationSlug', slug);
    }
  }, [organizationName, slugManuallyEdited, setValue]);

  // Check slug availability
  useEffect(() => {
    const checkSlug = async () => {
      if (!organizationSlug || organizationSlug.length < 3) {
        setSlugStatus('idle');
        return;
      }

      // Check reserved slugs locally first
      if (RESERVED_SLUGS.includes(organizationSlug as any)) {
        setSlugStatus('taken');
        return;
      }

      setSlugStatus('checking');

      try {
        const response = await authApi.checkSlug(organizationSlug);
        setSlugStatus(response.data.available ? 'available' : 'taken');
      } catch {
        setSlugStatus('idle');
      }
    };

    const debounce = setTimeout(checkSlug, 500);
    return () => clearTimeout(debounce);
  }, [organizationSlug]);

  const onSubmit = async (data: RegisterFormData) => {
    if (slugStatus === 'taken') {
      setError('This URL is already taken. Please choose a different one.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let serviceTypeId = data.serviceTypeId;

      // If it's a custom service type, create it first
      if (selectedServiceType?.isCustom && selectedServiceType?.inputValue) {
        const result = await serviceTypesApi.create(selectedServiceType.inputValue);
        serviceTypeId = result.serviceType._id;
        // Update the service types list with the new type
        if (result.isNew) {
          setServiceTypes((prev) => [...prev, result.serviceType]);
        }
      }

      await registerUser({ ...data, serviceTypeId });
      navigate('/dashboard');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: COLORS.background,
      }}
    >
      {/* Header */}
      <Box sx={{ py: 2, px: 3 }}>
        <Typography
          component={RouterLink}
          to="/"
          variant="h5"
          sx={{
            fontFamily: 'Georgia, serif',
            fontWeight: 400,
            color: COLORS.primary,
            textDecoration: 'none',
          }}
        >
          Elevair
        </Typography>
      </Box>

      {/* Register Form */}
      <Container maxWidth="sm" sx={{ flex: 1, py: 4 }}>
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom textAlign="center">
              Start Your Free Trial
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
              sx={{ mb: 4 }}
            >
              14 days free • No credit card required
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    {...register('firstName')}
                    label="First Name"
                    fullWidth
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    autoComplete="given-name"
                    autoFocus
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    {...register('lastName')}
                    label="Last Name"
                    fullWidth
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    autoComplete="family-name"
                  />
                </Grid>
              </Grid>

              <TextField
                {...register('email')}
                label="Email"
                type="text"
                fullWidth
                margin="normal"
                error={!!errors.email}
                helperText={errors.email?.message}
                autoComplete="email"
              />

              <TextField
                {...register('password')}
                label="Password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                margin="normal"
                error={!!errors.password}
                helperText={errors.password?.message}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                {...register('organizationName')}
                label="Business Name"
                fullWidth
                margin="normal"
                error={!!errors.organizationName}
                helperText={errors.organizationName?.message}
                placeholder="e.g., Sarah's Services"
              />

              <Autocomplete
                value={selectedServiceType}
                inputValue={serviceTypeInput}
                onInputChange={(_, newInputValue) => {
                  setServiceTypeInput(newInputValue);
                }}
                onBlur={() => {
                  // If user typed something custom and didn't select, auto-create custom option
                  if (serviceTypeInput && !selectedServiceType) {
                    const isExisting = serviceTypes.some(
                      (st) => st.name.toLowerCase() === serviceTypeInput.toLowerCase()
                    );
                    if (!isExisting) {
                      const customOption: ServiceTypeOption = {
                        _id: 'custom',
                        name: serviceTypeInput,
                        slug: '',
                        inputValue: serviceTypeInput,
                        isCustom: true,
                      };
                      setSelectedServiceType(customOption);
                      setValue('serviceTypeId', 'custom');
                    }
                  }
                }}
                onChange={(_, newValue) => {
                  if (typeof newValue === 'string') {
                    // User typed and pressed enter
                    const customOption: ServiceTypeOption = {
                      _id: 'custom',
                      name: newValue,
                      slug: '',
                      inputValue: newValue,
                      isCustom: true,
                    };
                    setSelectedServiceType(customOption);
                    setValue('serviceTypeId', 'custom');
                  } else if (newValue && newValue.inputValue) {
                    // User selected "Add: xxx" option
                    const customOption: ServiceTypeOption = {
                      _id: 'custom',
                      name: newValue.inputValue,
                      slug: '',
                      inputValue: newValue.inputValue,
                      isCustom: true,
                    };
                    setSelectedServiceType(customOption);
                    setValue('serviceTypeId', 'custom');
                  } else {
                    // User selected an existing option
                    setSelectedServiceType(newValue);
                    setValue('serviceTypeId', newValue?._id || '');
                  }
                }}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);
                  const { inputValue } = params;
                  // Suggest creating a new value if it doesn't exist
                  const isExisting = options.some(
                    (option) => option.name.toLowerCase() === inputValue.toLowerCase()
                  );
                  if (inputValue !== '' && !isExisting) {
                    filtered.push({
                      _id: 'add-new',
                      name: `Add "${inputValue}"`,
                      slug: '',
                      inputValue,
                      isCustom: true,
                    });
                  }
                  return filtered;
                }}
                selectOnFocus
                handleHomeEndKeys
                freeSolo
                options={serviceTypes as ServiceTypeOption[]}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    return option;
                  }
                  if (option.inputValue) {
                    return option.inputValue;
                  }
                  return option.icon ? `${option.icon} ${option.name}` : option.name;
                }}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <li key={option._id} {...otherProps}>
                      {option.isCustom
                        ? option.name
                        : option.icon
                        ? `${option.icon} ${option.name}`
                        : option.name}
                    </li>
                  );
                }}
                loading={serviceTypesLoading}
                disabled={serviceTypesLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Service Type"
                    margin="normal"
                    error={!!errors.serviceTypeId}
                    helperText={
                      errors.serviceTypeId?.message ||
                      'Select from the list or type your own'
                    }
                  />
                )}
              />

              <TextField
                {...register('organizationSlug', {
                  onChange: () => setSlugManuallyEdited(true),
                })}
                label="Your Elevair Landing Page URL"
                fullWidth
                margin="normal"
                error={!!errors.organizationSlug || slugStatus === 'taken'}
                helperText={
                  errors.organizationSlug?.message ||
                  (slugStatus === 'taken'
                    ? 'This URL is already taken'
                    : `elevair.app/${organizationSlug || 'your-business'}`)
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">elevair.app/</InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {slugStatus === 'checking' && (
                        <CircularProgress size={20} />
                      )}
                      {slugStatus === 'available' && (
                        <CheckCircle color="success" />
                      )}
                      {slugStatus === 'taken' && <Cancel color="error" />}
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isLoading || slugStatus === 'taken'}
                sx={{ mt: 3, py: 1.5 }}
              >
                {isLoading ? 'Creating your account...' : 'Create Account'}
              </Button>

              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ mt: 3 }}
              >
                Already have an account?{' '}
                <Link component={RouterLink} to="/login" underline="hover">
                  Sign in
                </Link>
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
                display="block"
                sx={{ mt: 2 }}
              >
                By creating an account, you agree to our{' '}
                <Link component={RouterLink} to="/terms" underline="hover">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link component={RouterLink} to="/privacy" underline="hover">
                  Privacy Policy
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
